import React, { useMemo } from 'react';
import { Box, Button, Image, Typography } from '@lucid/lego';
import type { TransformedCheckoutDetail } from '../../../api/services/lupp/inventory/transformers.checkout';
import Feature from './Feature/Feature';
import Price from './Price/Price';
import styles from './VehicleCard.module.scss';
import { DOCUSIGN_SENT_STATUSES, PARTNER_COMPENSATION_PERCENTAGE } from '../constants';
import { parseLocalePrice } from '../../../utils/tools';

/**
 * Labels type for i18n support
 */
type Labels = Record<string, string> | undefined;

type Theme = 'dark' | 'light';

type PricingSummaryItem = {
  label: string;
  value: number;
};

type VehicleCardProps = {
  /** Theme for the component */
  theme: Theme;
  /** Checkout data from the API */
  checkoutData?: TransformedCheckoutDetail | null;
  /** Quote ID - required for download functionality */
  quoteId?: string;
  /** Callback when download quotation button is clicked */
  onDownloadQuotation?: () => void;
  /** Callback when send docusign button is clicked */
  onSendDocusign?: () => void;
  /** Current docusign status */
  docusignStatus?: string;
  /** Whether the quote has been saved successfully */
  isQuoteSaved?: boolean;
  /** Current status of the quote - buttons disabled when not 'Active' */
  currentStatus?: string;
  /** Whether the vehicle is unavailable */
  isVehicleUnavailable?: boolean;
  /** Cash discount amount entered in the form */
  cashDiscount?: string;
  /** Percentage discount entered in the form */
  percentageDiscount?: string;
  /** Agent subvention amount entered in the form */
  agentSubventionAmount?: string;
  /** Preferred payment method selected by the user */
  preferredPaymentMethod?: string;
  /** Test ID for testing */
  dataTestId?: string;
  /** i18n labels from Contentful */
  labels?: Labels;
};

/**
 * Vehicle card component displaying vehicle configuration and pricing summary
 * @component
 * @param {VehicleCardProps} props - Component props
 * @returns {JSX.Element} Vehicle card with image, variant details, pricing, and action buttons
 * @example
 * <VehicleCard
 *   theme="dark"
 *   checkoutData={checkoutData}
 *   quoteId="quote-123"
 *   onDownloadQuotation={handleDownload}
 *   onSendDocusign={handleSendDocusign}
 *   dataTestId="vehicle-card"
 * />
 */

const VehicleCard: React.FC<VehicleCardProps> = ({
  theme,
  checkoutData,
  quoteId,
  onDownloadQuotation,
  onSendDocusign,
  docusignStatus,
  isQuoteSaved = false,
  currentStatus,
  isVehicleUnavailable = false,
  cashDiscount,
  percentageDiscount,
  agentSubventionAmount,
  preferredPaymentMethod,
  dataTestId = 'vehicle-card',
  labels,
}) => {
  // Disable buttons when currentStatus exists and is not 'Active'
  const isNotActive = currentStatus ? currentStatus !== 'Active' : false;

  // Calculate price adjustment based on form inputs - memoized for performance
  const priceAdjustment = useMemo(() => {
    const subtotal = checkoutData?.pricing?.configurationSubtotal ?? 0;
    let cashDiscountValue = parseLocalePrice(cashDiscount);
    let percentageDiscountValue = parseLocalePrice(percentageDiscount);
    const agentSubventionValue = parseLocalePrice(agentSubventionAmount);

    const isCashDiscountInAmount = cashDiscountValue > 0;
    let isPercentageDiscountExceed = false;

    // Calculate cash discount from percentage if no cash amount entered
    if (!isCashDiscountInAmount && percentageDiscountValue > 0) {
      // "percentage"
      const clampedPercentage = Math.min(percentageDiscountValue, 100);
      isPercentageDiscountExceed = clampedPercentage > PARTNER_COMPENSATION_PERCENTAGE;
      cashDiscountValue = (clampedPercentage / 100) * subtotal;
    } else if (isCashDiscountInAmount) {
      // "amount"
      const clampedCash = Math.min(cashDiscountValue, subtotal);
      percentageDiscountValue = (clampedCash / subtotal) * 100;
      isPercentageDiscountExceed = percentageDiscountValue > PARTNER_COMPENSATION_PERCENTAGE;
    }

    // Use either Cash/Percentage Discount OR Agent Subvention Amount (mutually exclusive)
    // Cash Discount is for 'Cash' and '3rd Party Financing' payment methods
    // Agent Subvention is for 'Loan', 'Leasing', etc. payment methods
    const totalDiscountValue =
      cashDiscountValue > 0 || percentageDiscountValue > 0
        ? cashDiscountValue
        : agentSubventionValue;

    return { discountValue: totalDiscountValue, isPercentageDiscountExceed };
  }, [
    checkoutData?.pricing?.configurationSubtotal,
    cashDiscount,
    percentageDiscount,
    agentSubventionAmount,
  ]);

  // VAT calculation based on subtotal after price adjustments - memoized for performance
  const vatCalculation = useMemo(() => {
    // Use configurationSubtotal (Net) if available, otherwise derive from subtotal (Gross)
    const grossSubtotal = checkoutData?.pricing?.subtotal ?? 0;
    const taxRatePercent = checkoutData?.pricing?.taxRate ?? 0;
    const vatRate = taxRatePercent / 100;

    const netSubtotal =
      checkoutData?.pricing?.configurationSubtotal ??
      (vatRate > 0 ? grossSubtotal / (1 + vatRate) : grossSubtotal);

    // Apply price adjustment directly to net subtotal (exc. VAT)
    // Formula: discounted value = subtotal (exc. VAT) - price adjustment
    // Skip price adjustment for LFS Lease payment method
    const isLfsLease = preferredPaymentMethod?.toLowerCase() === 'lfs lease';
    const priceAdjustmentAmount = isLfsLease ? 0 : priceAdjustment?.discountValue || 0;
    const discountedSubtotal = netSubtotal - priceAdjustmentAmount;

    // Calculate VAT on the discounted value
    // Formula: VAT = taxRate% × discounted value
    const roundedVat = Math.round((discountedSubtotal * vatRate + Number.EPSILON) * 100) / 100;

    // Calculate final subtotal with VAT
    // Formula: Subtotal = Subtotal (excl. VAT) - Price Adjustment + VAT
    // Which expands to: (netSubtotal - priceAdjustmentAmount) + roundedVat
    const subtotalWithVat = discountedSubtotal + roundedVat;

    // Calculate grand total by subtracting credits
    const totalCredits =
      checkoutData?.pricing?.credits?.reduce((sum, credit) => sum + credit.value, 0) ?? 0;
    const grandTotal = subtotalWithVat - totalCredits;

    return { roundedVat, adjustedSubtotal: subtotalWithVat, grandTotal, taxRatePercent };
  }, [
    checkoutData?.pricing?.subtotal,
    checkoutData?.pricing?.configurationSubtotal,
    checkoutData?.pricing?.credits,
    checkoutData?.pricing?.taxRate,
    priceAdjustment?.discountValue,
    preferredPaymentMethod,
  ]);

  // Shared button state calculations
  const hasQuote = Boolean(quoteId || isQuoteSaved);
  const isDocusignAlreadySent = docusignStatus
    ? DOCUSIGN_SENT_STATUSES.includes(docusignStatus)
    : false;
  const isDocusignDisabled =
    !hasQuote || isNotActive || isDocusignAlreadySent || isVehicleUnavailable;

  if (!checkoutData) {
    return null;
  }

  return (
    <Box className={styles.cardContainer} data-testid={dataTestId}>
      {/* Vehicle Image */}
      {checkoutData.vehicleImage && (
        <Box className={styles.vehicleImageContainer}>
          <Image
            uri_desktop={{ '1x': checkoutData.vehicleImage, '2x': checkoutData.vehicleImage }}
            uri_mobile={{ '1x': checkoutData.vehicleImage, '2x': checkoutData.vehicleImage }}
            alt={`${checkoutData.model} ${checkoutData.trim}`}
            className={styles.vehicleImage}
            aspectRatio={{ ratio: '16:10' }}
            rounded={false}
          />
        </Box>
      )}
      <Box>
        {/* Vehicle Variant Details */}
        {checkoutData?.vehicleOptions.map((option) => (
          <Feature
            key={option.code}
            description={option.name}
            price={option.value}
            theme={theme}
            labels={labels}
          />
        ))}

        {/* Features (sound system, driver assistance, packages, seating, etc.) */}
        {checkoutData?.features.map((feature) => (
          <Feature
            key={feature.code}
            description={feature.name}
            price={feature.value}
            theme={theme}
            labels={labels}
          />
        ))}

        {/* Divider */}
        <Box className={styles.divider} mt={16} mb={16} />
        {/* Configuration Subtotal */}
        <Box className={styles.subtotalRow} mt={8}>
          <Price
            label={labels?.['vehicle.subtotalExclVat'] ?? 'Subtotal (excl. VAT, and other costs)'}
            value={checkoutData?.pricing?.configurationSubtotal ?? 0}
            theme={theme}
            isBold={true}
            skipFormatting
          />
        </Box>
      </Box>
      {/* Pricing Summary */}
      <Box className={styles.pricingSummary} mt={8}>
        {/* Divider */}
        <Box className={styles.divider} mt={16} mb={16} />
        <Price label={checkoutData.trim} value={checkoutData.pricing.basePrice} theme={theme} />
        <Price
          label={labels?.['vehicle.options'] ?? 'Options'}
          value={checkoutData.pricing.addOns}
          theme={theme}
          skipFormatting
        />

        {/* Fees */}
        {checkoutData.pricing.fees.map((fee: PricingSummaryItem, index: number) => (
          <Price key={`fee-${index}`} label={fee.label} value={fee.value} theme={theme} />
        ))}
        {/* Deposit */}
        {/* <Price
          label="Deposit"
          value={checkoutData?.pricing?.deposit || 0}

          theme={theme}
        /> */}
        {/* Price Adjustment - hidden for LFS Lease */}
        {preferredPaymentMethod?.toLowerCase() !== 'lfs lease' && (
          <Price
            label={labels?.['vehicle.priceAdjustment'] ?? 'Price Adjustment'}
            value={priceAdjustment?.discountValue || 0}
            theme={theme}
            isNegative
            skipFormatting={true}
          />
        )}

        {/* Calculate VAT */}
        <Price
          label={`VAT (${vatCalculation.taxRatePercent}%)`}
          value={vatCalculation.roundedVat}
          theme={theme}
          skipFormatting
        />

        {/* Calculated New Subtotal Amount */}
        <Price
          label={labels?.['vehicle.subtotal'] ?? 'Subtotal'}
          value={vatCalculation?.adjustedSubtotal || 0}
          theme={theme}
          isBold
          skipFormatting
        />

        {/* Credits */}
        {checkoutData.pricing.credits.map((credit: PricingSummaryItem, index: number) => (
          <Price
            key={`credit-${index}`}
            label={credit.label}
            value={credit.value}
            theme={theme}
            isNegative
          />
        ))}

        {/* Divider */}
        <Box className={styles.divider} mt={16} mb={16} />

        {/* Total */}
        <Price
          label={labels?.['vehicle.total'] ?? 'Total*'}
          value={vatCalculation?.grandTotal || 0}
          theme={theme}
          isBold
          skipFormatting
        />

        <Typography variant="body" size="small" color="gray-5" theme={theme}>
          {labels?.['vehicle.totalNote'] ??
            '* Total price including VAT, destination, roadworthy, delivery, documentation and registration costs.'}
        </Typography>
      </Box>
      {/* Action Buttons */}
      <Box className={styles.buttonContainer} pt={24} pb={24} pl={2} pr={2}>
        <Button
          label={labels?.['vehicle.downloadQuotation'] ?? 'Download Quotation'}
          type="secondary"
          color={theme === 'dark' ? 'neutral' : 'brand'}
          size="medium"
          theme={theme === 'dark' ? 'light' : 'dark'}
          className={theme === 'light' ? styles.downloadButtonLight : undefined}
          handleClick={hasQuote ? onDownloadQuotation : undefined}
          disabled={!hasQuote}
          gaComponent="vehicle-card"
          dataTestId={`${dataTestId}-download-btn`}
        />
        <Button
          label={labels?.['vehicle.sendDocusign'] ?? 'Send Docusign'}
          type="primary"
          color={theme === 'dark' ? 'neutral' : 'brand'}
          size="medium"
          theme={theme === 'dark' ? 'light' : 'dark'}
          handleClick={!isDocusignDisabled ? onSendDocusign : undefined}
          disabled={isDocusignDisabled}
          gaComponent="vehicle-card"
          dataTestId={`${dataTestId}-send-btn`}
        />
      </Box>
    </Box>
  );
};

VehicleCard.displayName = 'VehicleCard';

export default VehicleCard;
